// Utility functions for generating correct tree paths and handling tree path related issues

/**
 * Generate a tree path for a given location
 * @param {Object} params - Parameters for tree path generation
 * @param {number} sectionIndex - Section index (0-based)
 * @param {number} stepIndex - Step index (0-based)
 * @param {number} substepIndex - Substep index (0-based, optional)
 * @param {string} nodeType - Type of node (richText, attachment, canvas, video, form)
 * @param {number} nodeIndex - Node index (0-based)
 * @returns {string} - Formatted tree path
 */
export const generateTreePath = ({
  sectionIndex,
  stepIndex,
  substepIndex = undefined,
  nodeType = undefined,
  nodeIndex = undefined,
}) => {
  const sectionNumber = sectionIndex + 1;
  const stepNumber = stepIndex + 1;

  let path = `Section ${sectionNumber} > Step ${stepNumber}`;

  if (substepIndex !== undefined) {
    const substepLetter = String.fromCharCode(65 + substepIndex); // A, B, C, etc.
    path += ` > Substep ${stepNumber}${substepLetter}`;
  }

  if (nodeType && nodeIndex !== undefined) {
    const nodeNumber = nodeIndex + 1;
    const nodeTypeDisplay = getNodeTypeDisplay(nodeType);
    path += ` > ${nodeTypeDisplay} ${nodeNumber}`;
  }

  return path;
};

/**
 * Get the display name for a node type
 * @param {string} nodeType - The node type
 * @returns {string} - Display name for the node type
 */
export const getNodeTypeDisplay = (nodeType) => {
  const typeMap = {
    richText: "Rich Text",
    rich_text: "Rich Text",
    attachment: "Attachment",
    canvas: "Canvas",
    video: "Video",
    form: "Form",
  };

  return typeMap[nodeType] || nodeType;
};

/**
 * Generate tree path for source location
 * @param {Object} locationState - Location state object
 * @param {Array} sortedSections - Sorted sections array
 * @param {Object} stepsBySection - Steps by section object
 * @param {Object} substepsByStep - Substeps by step object
 * @returns {string} - Source tree path
 */
export const generateSourceTreePath = (
  locationState,
  sortedSections,
  stepsBySection,
  substepsByStep
) => {
  const {
    sourceSectionIndex,
    sourceStepIndex,
    sourceSubstepIndex,
    sourceNodeIndex,
    sourceNodeType,
  } = locationState;

  if (sourceSectionIndex === undefined || sourceStepIndex === undefined) {
    return "Unknown Source";
  }

  return generateTreePath({
    sectionIndex: sourceSectionIndex,
    stepIndex: sourceStepIndex,
    substepIndex: sourceSubstepIndex,
    nodeType: sourceNodeType,
    nodeIndex: sourceNodeIndex,
  });
};

/**
 * Generate tree path for target location
 * @param {Object} locationState - Location state object
 * @param {Array} sortedSections - Sorted sections array
 * @param {Object} stepsBySection - Steps by section object
 * @param {Object} substepsByStep - Substeps by step object
 * @returns {string} - Target tree path
 */
export const generateTargetTreePath = (
  locationState,
  sortedSections,
  stepsBySection,
  substepsByStep
) => {
  const {
    targetSectionIndex,
    targetStepIndex,
    targetSubstepIndex,
    targetNodeIndex,
    targetNodeType,
  } = locationState;

  if (targetSectionIndex === undefined || targetStepIndex === undefined) {
    return "Unknown Target";
  }

  return generateTreePath({
    sectionIndex: targetSectionIndex,
    stepIndex: targetStepIndex,
    substepIndex: targetSubstepIndex,
    nodeType: targetNodeType,
    nodeIndex: targetNodeIndex,
  });
};

/**
 * Get correct index number for display (1-based for users)
 * @param {number} index - 0-based index
 * @returns {number} - 1-based index for display
 */
export const getDisplayIndex = (index) => {
  return (index || 0) + 1;
};

/**
 * Get substep letter from index
 * @param {number} substepIndex - 0-based substep index
 * @returns {string} - Substep letter (A, B, C, etc.)
 */
export const getSubstepLetter = (substepIndex) => {
  if (substepIndex === undefined) return "";
  return String.fromCharCode(65 + substepIndex);
};

/**
 * Generate status text for move/copy operations
 * @param {string} operation - Operation type ('move' or 'copy')
 * @param {string} view - View type ('new' or 'original')
 * @returns {string} - Status text
 */
export const generateStatusText = (operation, view) => {
  const operationText = operation === "copy" ? "Copied" : "Moved";
  const viewText = view === "new" ? "New View" : "Original View";
  return `${operationText} - ${viewText}`;
};

/**
 * Generate complete tree path with node details
 * @param {Object} params - Parameters for complete tree path
 * @param {number} sectionIndex - Section index
 * @param {number} stepIndex - Step index
 * @param {number} substepIndex - Substep index (optional)
 * @param {string} nodeType - Node type (optional)
 * @param {number} nodeIndex - Node index (optional)
 * @param {string} nodeDescription - Node description (optional)
 * @returns {string} - Complete tree path with details
 */
export const generateCompleteTreePath = ({
  sectionIndex,
  stepIndex,
  substepIndex = undefined,
  nodeType = undefined,
  nodeIndex = undefined,
  nodeDescription = undefined,
}) => {
  const sectionNumber = getDisplayIndex(sectionIndex);
  const stepNumber = getDisplayIndex(stepIndex);

  let path = `Section ${sectionNumber} > Step ${stepNumber}`;

  if (substepIndex !== undefined) {
    const substepLetter = getSubstepLetter(substepIndex);
    path += ` > Substep ${stepNumber}${substepLetter}`;
  }

  // Removed child node from tree path - only show section > step > substep
  // if (nodeType && nodeIndex !== undefined) {
  //   const nodeNumber = getDisplayIndex(nodeIndex);
  //   const nodeTypeDisplay = getNodeTypeDisplay(nodeType);
  //   path += ` > ${nodeTypeDisplay} ${nodeNumber}`;

  //   if (nodeDescription) {
  //     path += ` (${nodeDescription})`;
  //   }
  // }

  return path;
};

/**
 * Validate and correct tree path data
 * @param {Object} locationState - Location state object
 * @param {Array} sortedSections - Sorted sections array
 * @param {Object} stepsBySection - Steps by section object
 * @param {Object} substepsByStep - Substeps by step object
 * @returns {Object} - Corrected location state
 */
export const validateAndCorrectTreePathData = (
  locationState,
  sortedSections,
  stepsBySection,
  substepsByStep
) => {
  const corrected = { ...locationState };

  // Ensure section indices are valid
  if (
    corrected.sourceSectionIndex !== undefined &&
    (corrected.sourceSectionIndex < 0 ||
      corrected.sourceSectionIndex >= sortedSections.length)
  ) {
    console.warn("Invalid sourceSectionIndex, resetting to 0");
    corrected.sourceSectionIndex = 0;
  }

  if (
    corrected.targetSectionIndex !== undefined &&
    (corrected.targetSectionIndex < 0 ||
      corrected.targetSectionIndex >= sortedSections.length)
  ) {
    console.warn("Invalid targetSectionIndex, resetting to 0");
    corrected.targetSectionIndex = 0;
  }

  // Ensure step indices are valid
  if (corrected.sourceStepIndex !== undefined) {
    const sourceSectionId =
      sortedSections[corrected.sourceSectionIndex]?.section_tree_node_id;
    const sourceSteps = stepsBySection[sourceSectionId] || [];
    if (
      corrected.sourceStepIndex < 0 ||
      corrected.sourceStepIndex >= sourceSteps.length
    ) {
      console.warn("Invalid sourceStepIndex, resetting to 0");
      corrected.sourceStepIndex = 0;
    }
  }

  if (corrected.targetStepIndex !== undefined) {
    const targetSectionId =
      sortedSections[corrected.targetSectionIndex]?.section_tree_node_id;
    const targetSteps = stepsBySection[targetSectionId] || [];
    if (
      corrected.targetStepIndex < 0 ||
      corrected.targetStepIndex >= targetSteps.length
    ) {
      console.warn("Invalid targetStepIndex, resetting to 0");
      corrected.targetStepIndex = 0;
    }
  }

  // Ensure substep indices are valid
  if (corrected.sourceSubstepIndex !== undefined) {
    const sourceStepId =
      sortedSections[corrected.sourceSectionIndex]?.section_tree_node_id;
    const sourceSubsteps = substepsByStep[sourceStepId] || [];
    if (
      corrected.sourceSubstepIndex < 0 ||
      corrected.sourceSubstepIndex >= sourceSubsteps.length
    ) {
      console.warn("Invalid sourceSubstepIndex, resetting to 0");
      corrected.sourceSubstepIndex = 0;
    }
  }

  if (corrected.targetSubstepIndex !== undefined) {
    const targetStepId =
      sortedSections[corrected.targetSectionIndex]?.section_tree_node_id;
    const targetSubsteps = substepsByStep[targetStepId] || [];
    if (
      corrected.targetSubstepIndex < 0 ||
      corrected.targetSubstepIndex >= targetSubsteps.length
    ) {
      console.warn("Invalid targetSubstepIndex, resetting to 0");
      corrected.targetSubstepIndex = 0;
    }
  }

  return corrected;
};

/**
 * Optimized tree path display component for consistent styling
 * @param {Object} params - Tree path parameters
 * @returns {JSX.Element} - Tree path display component
 */
export const TreePathDisplay = ({
  sectionIndex,
  stepIndex,
  substepIndex,
  nodeType,
  nodeIndex,
}) => (
  <div
    style={{
      color: "#888",
      fontSize: "14px",
      fontWeight: "normal",
    }}
  >
    {generateCompleteTreePath({
      sectionIndex,
      stepIndex,
      substepIndex,
      nodeType,
      nodeIndex,
    })}
  </div>
);

/**
 * Optimized heading container with tree path
 * @param {Object} params - Heading parameters
 * @returns {JSX.Element} - Heading with tree path
 */
export const HeadingWithTreePath = ({
  title,
  treePathParams,
  children,
  fontSize = "20px",
  color,
}) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
    }}
  >
    <h2 style={{ 
      margin: 0, 
      fontSize: fontSize,
      color: color || "inherit" // Apply color if provided, otherwise inherit
    }}>{title}</h2>
    <TreePathDisplay {...treePathParams} />
    {children}
  </div>
);

/**
 * Optimized status display component
 * @param {string} status - Status value from location.state
 * @returns {JSX.Element} - Status display component
 */
export const StatusDisplay = ({ status }) => (
  <div>
    <strong>Status: </strong>
    <span style={{ color: "orange" }}>{status}</span>
  </div>
);

/**
 * Optimized revision display component
 * @param {string} revision - Revision value
 * @returns {JSX.Element} - Revision display component
 */
export const RevisionDisplay = ({ revision }) => (
  <div>
    <strong>Revision: </strong>
    <span>{revision}</span>
  </div>
);
